/**
 * Utility functions and types for PerformanceAnalytics component
 *
 * Contains pure functions for data transformation and type definitions.
 * Exports `safeRound` for shared null-safe rounding across the feature.
 * @remarks Score extraction functions return null for missing data instead of
 * numeric defaults, allowing the view layer to hide missing data points.
 * Custom organization metrics from bioData.customStats are dynamically
 * included in score extraction via `extractCustomMetricScores`.
 */

import {
  PlayerDetailPlayerMetaFragment,
  PlayerDetailBasicCompetitionStatsFragment,
  PerformanceAnalyticsPlayerScoresFragment,
} from "@/generated/graphql";
import type { BackendCustomStats } from "@/types/customMetrics";
import { processDynamicCustomMetrics } from "@/types/customMetrics";

import {
  ALL_CATEGORICAL_METRICS,
  GK_CATEGORICAL_METRICS,
  OUTFIELD_CATEGORICAL_METRICS,
  type CategoricalMetricDefinition,
} from "../metricDefinitions";
import type {
  CompareItem,
  ComparisonDataStatus,
  DistributionComparisonStatus,
  TimeframeOption,
} from "../types";

import type { BeeSwarmInput } from "./beeSwarmPositioning";

/**
 * Safely round a value, propagating null for missing data
 * @param value - Numeric value or null/undefined
 * @returns Rounded number or null when data is missing
 */
export const safeRound = (value: number | null | undefined): number | null =>
  value != null ? Math.round(value) : null;

/**
 * Build a label→score record for a set of categorical metrics (SE-4168).
 * Reads each metric's field from `source` — `bioData` for the "global"
 * (career / time-decayed) variant, or a `seasonCategoricalScores` entry for the
 * "positional" (per-season) variant. Non-numeric/missing values become null so
 * the view can hide them, matching the previous per-metric extraction behaviour.
 * @param definitions - Categorical metric definitions to read
 * @param source - Object holding the score fields (bioData or a season entry)
 * @param variant - Which field to read: career "global" or per-season "positional"
 * @returns Record keyed by metric display label with rounded scores or null
 */
export const buildCategoricalScores = (
  definitions: readonly CategoricalMetricDefinition[],
  source: Record<string, unknown> | null | undefined,
  variant: "global" | "positional"
): Record<string, number | null> =>
  definitions.reduce<Record<string, number | null>>((acc, def) => {
    const field = variant === "global" ? def.globalField : def.positionalField;
    const raw = source?.[field];
    return {
      ...acc,
      [def.label]: safeRound(typeof raw === "number" ? raw : null),
    };
  }, {});

/** Search player edge from GraphQL query */
interface SearchPlayerEdge {
  readonly node: {
    readonly id: string;
    readonly name?: string | null;
    readonly firstName?: string | null;
    readonly lastName?: string | null;
    readonly knownName?: string | null;
    readonly team?: { readonly name?: string | null } | null;
    readonly positions?:
      | readonly { readonly abbreviation?: string | null }[]
      | null;
  };
}

/**
 * Transform search player edges into CompareItem array
 * @param edges - Search player edges from GraphQL
 * @returns Array of CompareItem for player comparison
 */
export const transformSearchPlayersToCompareItems = (
  edges?: readonly SearchPlayerEdge[] | null
): CompareItem[] => {
  return (
    edges
      ?.map(edge => ({
        id: edge.node.id,
        name: edge.node?.name ?? "",
        team: edge.node.team?.name ?? undefined,
        position: edge.node.positions?.[0]?.abbreviation ?? undefined,
      }))
      // Drop entries with no backend name (SE-5469) — preserves the prior
      // behavior of filtering the "Unknown Player" placeholder that the
      // removed client-side name assembly used to produce for unnamed nodes.
      .filter(
        player => player.name !== "" && player.name !== "Unknown Player"
      ) ?? []
  );
};

/** Watchlist edge structure */
interface WatchlistEdge {
  readonly node: {
    readonly watchlist: { readonly id: string; readonly name: string };
  };
}

/**
 * Extract CompareItems from watchlist edges
 * @param edges - Array of watchlist edges
 * @returns Array of CompareItem
 */
export const extractCompareItemsFromWatchlistEdges = (
  edges?: readonly WatchlistEdge[] | null
): readonly CompareItem[] =>
  edges?.map(edge => ({
    id: edge.node.watchlist.id,
    name: edge.node.watchlist.name,
  })) ?? [];

/** Career option for aggregated metrics across all seasons */
const CAREER_OPTION: TimeframeOption = {
  id: "career",
  label: "Career",
  seasonId: null,
};

/**
 * Build season options from player's season categorical scores.
 * Includes a "Career" option first for aggregated cross-season metrics.
 * @param player - Player data fragment
 * @returns Array of timeframe options for season selection, with Career first
 */
export const buildSeasonOptions = (
  player?: PlayerDetailPlayerMetaFragment | null
): readonly TimeframeOption[] => {
  const seasons = player?.seasonCategoricalScores ?? [];
  const sortedSeasons = [...seasons].sort(
    (a, b) => (b.ggUnifiedSeasonKey ?? 0) - (a.ggUnifiedSeasonKey ?? 0)
  );

  // Reduce to array directly to preserve descending sort order
  // (Object.values reorders numeric keys ascending per ECMA-262)
  const seasonOptions = sortedSeasons
    .filter(s => s.ggUnifiedSeasonKey != null)
    .reduce<readonly TimeframeOption[]>((acc, s) => {
      const year = s.ggUnifiedSeasonKey ?? 0;
      if (acc.some(opt => opt.id === String(year))) return acc;
      const startYear = String(year).slice(-2);
      const endYear = String(year + 1).slice(-2);
      return [
        ...acc,
        {
          id: String(year),
          label: `Season ${startYear}/${endYear}`,
          seasonId: s.seasonId ?? null,
        },
      ];
    }, []);

  return seasonOptions.length > 0 ? [CAREER_OPTION, ...seasonOptions] : [];
};

/**
 * Build a seasonId UUID → year integer lookup.
 * Uses `seasonCategoricalScores` when available (exact mapping).
 * For GKs (empty categorical scores), correlates basic stats seasonIds
 * sorted by total appearances with `seasonWeightedAvgMetrics` years
 * sorted descending — most-active season maps to most-recent year.
 * @param player - Player data fragment
 * @param basicStats - BasicCompetitionStats for appearance-based sorting
 * @returns Map from seasonId UUID to ggUnifiedSeasonKey year
 * @remarks GKs have no per-season categorical scores and near-identical
 * appearances every season (e.g. ~54 each), so an appearance-only sort is
 * unstable and the resulting seasonId→year mapping flips with backend row
 * order — producing duplicate per-season stats and generic "Season N" labels
 * (SE-4239). Ties are broken by the seasonId string so the mapping is
 * deterministic and total over the zipped range regardless of input order.
 */
export const buildSeasonIdToYearMap = (
  player?: PlayerDetailPlayerMetaFragment | null,
  basicStats: readonly PlayerDetailBasicCompetitionStatsFragment[] = []
): Record<string, number> => {
  // Best source: seasonCategoricalScores has both seasonId and year
  const categoricalScores = player?.seasonCategoricalScores ?? [];
  if (categoricalScores.length > 0) {
    return categoricalScores.reduce(
      (acc, s) =>
        s.seasonId && s.ggUnifiedSeasonKey != null
          ? { ...acc, [s.seasonId]: s.ggUnifiedSeasonKey }
          : acc,
      {} as Record<string, number>
    );
  }

  // Fallback: correlate basic stats seasonIds (by appearances desc)
  // with weighted avg years (desc) — most-active season = most-recent year
  const years = [
    ...new Set(
      (player?.seasonWeightedAvgMetrics ?? []).map(m => m.ggUnifiedSeasonKey)
    ),
  ].sort((a, b) => b - a);

  if (years.length === 0 || basicStats.length === 0) return {};

  // Group appearances by seasonId and sort descending
  const seasonAppearances = basicStats.reduce(
    (acc, s) => ({
      ...acc,
      [s.seasonId]:
        (acc[s.seasonId] ?? 0) + Math.round(s.basicAppearances ?? 0),
    }),
    {} as Record<string, number>
  );

  // Sort by appearances desc, breaking ties on the seasonId string so the
  // ordering is deterministic and independent of object insertion order.
  const seasonIdsByAppearances = Object.entries(seasonAppearances)
    .sort(([idA, a], [idB, b]) => b - a || (idA < idB ? 1 : idA > idB ? -1 : 0))
    .map(([seasonId]) => seasonId);

  const zipLength = Math.min(seasonIdsByAppearances.length, years.length);

  return seasonIdsByAppearances
    .slice(0, zipLength)
    .reduce(
      (acc, seasonId, index) => ({ ...acc, [seasonId]: years[index] }),
      {} as Record<string, number>
    );
};

/**
 * Format a year integer as "Season XX/YY" label
 * @param year - ggUnifiedSeasonKey year (e.g. 2024)
 * @returns Formatted label like "Season 24/25"
 */
const formatSeasonLabel = (year: number): string => {
  const startYear = String(year).slice(-2);
  const endYear = String(year + 1).slice(-2);
  return `Season ${startYear}/${endYear}`;
};

/**
 * Build fallback season options from `seasonWeightedAvgMetrics` when
 * `seasonCategoricalScores` is empty (e.g. goalkeepers on the DataHub).
 * Uses `ggUnifiedSeasonKey` for both the option ID and label formatting.
 * Includes a "Career" option first for aggregated cross-season metrics.
 * @param player - Player data fragment
 * @returns Array of timeframe options sorted by year descending, with null seasonId
 */
export const buildSeasonOptionsFromWeightedAvgMetrics = (
  player?: PlayerDetailPlayerMetaFragment | null
): readonly TimeframeOption[] => {
  const metrics = player?.seasonWeightedAvgMetrics ?? [];
  const uniqueYears = [...new Set(metrics.map(m => m.ggUnifiedSeasonKey))].sort(
    (a, b) => b - a
  );

  const seasonOptions = uniqueYears.map(year => ({
    id: String(year),
    label: formatSeasonLabel(year),
    seasonId: null,
  }));

  return seasonOptions.length > 0 ? [CAREER_OPTION, ...seasonOptions] : [];
};

/**
 * Build fallback season options from basic competition stats when
 * `seasonCategoricalScores` is empty (e.g. goalkeepers).
 * Seasons are sorted by total appearances (most active first).
 * When a seasonId-to-year map is provided, proper "Season XX/YY"
 * labels are used; otherwise falls back to "Current Season" / "Season N".
 * @param stats - BasicCompetitionStats array from the backend materialized view
 * @param seasonIdToYearMap - Optional lookup from seasonId UUID to year integer
 * @returns Array of timeframe options keyed by seasonId UUID
 */
export const buildSeasonOptionsFromBasicStats = (
  stats: readonly PlayerDetailBasicCompetitionStatsFragment[],
  seasonIdToYearMap: Record<string, number> = {}
): readonly TimeframeOption[] => {
  const seasonTotals = stats.reduce(
    (acc, s) => ({
      ...acc,
      [s.seasonId]:
        (acc[s.seasonId] ?? 0) + Math.round(s.basicAppearances ?? 0),
    }),
    {} as Record<string, number>
  );

  const sortedSeasonIds = Object.entries(seasonTotals)
    .sort(([, a], [, b]) => b - a)
    .map(([seasonId]) => seasonId);

  return sortedSeasonIds.map((seasonId, index) => {
    const year = seasonIdToYearMap[seasonId];
    const label = year
      ? formatSeasonLabel(year)
      : index === 0
        ? "Current Season"
        : `Season ${sortedSeasonIds.length - index}`;

    return { id: seasonId, label, seasonId };
  });
};

/**
 * View mode for the chart display
 */
export type ViewMode = "spider" | "dot";

/**
 * Metric option for selectors
 */
export interface MetricOption {
  readonly label: string;
  readonly value: number | null;
}

/**
 * Selected metrics for each axis position
 */
export interface SelectedMetrics {
  readonly [position: number]: MetricOption;
}

/**
 * Player data point for scatter plot
 */
export interface PlayerDataPoint {
  readonly id: string;
  readonly name: string;
  readonly x: number | null;
  readonly y: number | null;
  readonly isCurrentPlayer: boolean;
  /** Whether this is a comparison player (shows orange dot) */
  readonly isComparisonPlayer?: boolean;
}

/**
 * Comparison player data for scatter plot
 */
export interface ComparisonPlayerData {
  readonly id: string;
  readonly name: string;
  readonly scores: Record<string, number | null>;
}

/**
 * Active selector type for dot plot modal
 */
export type ActiveSelector = "x" | "y" | null;

/**
 * Player data for distribution charts
 */
export interface DistributionPlayer {
  readonly id: string;
  readonly name: string;
  readonly scores: Record<string, number | null>;
}

/**
 * Get distribution data for a metric with player info included
 * @param label - The metric label
 * @param playerValue - The player's value for this metric, null if missing
 * @param comparisonPlayers - Array of comparison players with scores and names
 * @returns Array of BeeSwarmInput, excluding players with null scores
 */
export const getDistributionData = (
  label: string,
  playerValue: number | null,
  comparisonPlayers: readonly DistributionPlayer[]
): readonly BeeSwarmInput[] => {
  const comparisonData: readonly BeeSwarmInput[] = comparisonPlayers
    .filter(p => p.scores[label] != null)
    .map(p => ({
      value: p.scores[label] as number,
      playerName: p.name,
      playerId: p.id,
    }));
  // Main player value added without name (it's highlighted separately)
  return playerValue != null
    ? [...comparisonData, { value: playerValue }]
    : comparisonData;
};

/** Default outfield scores used when player data is not available (null = missing) */
const DEFAULT_SCORES: Record<string, null> = {
  ...Object.fromEntries(OUTFIELD_CATEGORICAL_METRICS.map(m => [m.label, null])),
  "Physical Score": null,
  GPR: null,
};

/** Default GK scores used when player data is not available (null = missing) */
const GK_DEFAULT_SCORES: Record<string, null> = {
  ...Object.fromEntries(GK_CATEGORICAL_METRICS.map(m => [m.label, null])),
  "Physical Score": null,
  GPR: null,
};

/**
 * Extract custom metric scores from backend customStats JSONB
 * @param customStats - Backend custom stats object or null/undefined
 * @returns Record of display name to rounded-sm score, empty object if no custom stats
 */
export const extractCustomMetricScores = (
  customStats: BackendCustomStats | null | undefined
): Record<string, number | null> => {
  if (!customStats) return {};
  return processDynamicCustomMetrics(customStats).reduce(
    (acc, metric) => ({
      ...acc,
      [metric.displayName]: safeRound(metric.value),
    }),
    {} as Record<string, number | null>
  );
};

/**
 * Resolves the categorical metric definitions for a player's position.
 * @param isGoalkeeper - Whether the player is a goalkeeper
 * @returns GK or outfield categorical metric definitions
 */
const definitionsForPosition = (
  isGoalkeeper: boolean
): readonly CategoricalMetricDefinition[] =>
  isGoalkeeper ? GK_CATEGORICAL_METRICS : OUTFIELD_CATEGORICAL_METRICS;

/**
 * Merges categorical scores with the global-only Physical Score and GPR plus any
 * custom organization metrics — the shared shape every score extractor returns.
 * @param categorical - Categorical metric label→score record
 * @param physicalScore - Rounded physical score (global-only)
 * @param gpr - Rounded GPR (global-only)
 * @param customScores - Custom organization metric scores
 * @returns Combined label→score record
 */
export const withGlobalScores = (
  categorical: Record<string, number | null>,
  physicalScore: number | null,
  gpr: number | null,
  customScores: Record<string, number | null>
): Record<string, number | null> => ({
  ...categorical,
  "Physical Score": physicalScore,
  GPR: gpr,
  ...customScores,
});

/**
 * Extract scores for a comparison player based on selected season
 * Returns both outfield and GK metrics so the UI can pick the relevant set
 * @param player - Comparison player fragment with score data
 * @param seasonKey - Optional season key (e.g., "2024") for season-specific scores
 * @returns Object with scores for each metric (outfield + GK), null for missing data
 */
export const extractComparisonPlayerScores = (
  player: PerformanceAnalyticsPlayerScoresFragment,
  seasonKey?: string | null
): Record<string, number | null> => {
  // Physical score and GPR are global-only (no seasonal version)
  const physicalScore = safeRound(player.bioData?.physicalScore);
  const gpr = safeRound(player.bioData?.gpr);
  const customScores = extractCustomMetricScores(
    player.bioData?.customStats as BackendCustomStats | null | undefined
  );

  // If season is selected, try season-specific scores
  const seasonData = seasonKey
    ? player.seasonCategoricalScores?.find(
        s => String(s.ggUnifiedSeasonKey) === seasonKey
      )
    : undefined;

  const categorical = seasonData
    ? buildCategoricalScores(ALL_CATEGORICAL_METRICS, seasonData, "positional")
    : buildCategoricalScores(ALL_CATEGORICAL_METRICS, player.bioData, "global");

  return withGlobalScores(categorical, physicalScore, gpr, customScores);
};

/**
 * Extract player global scores from player data (compared to all players)
 * @param player - Player data fragment
 * @param isGoalkeeper - Whether to extract GK-specific metrics
 * @returns Object with global scores for each metric, null for missing data
 */
export const extractPlayerScores = (
  player?: PlayerDetailPlayerMetaFragment | null,
  isGoalkeeper = false
): Record<string, number | null> => {
  if (!player?.bioData) {
    return isGoalkeeper ? GK_DEFAULT_SCORES : DEFAULT_SCORES;
  }

  const bioData = player.bioData;

  const customScores = extractCustomMetricScores(
    bioData.customStats as BackendCustomStats | null | undefined
  );

  return withGlobalScores(
    buildCategoricalScores(
      definitionsForPosition(isGoalkeeper),
      bioData,
      "global"
    ),
    safeRound(bioData.physicalScore),
    safeRound(bioData.gpr),
    customScores
  );
};

/**
 * Extract player positional scores (compared to players in same position)
 * Positional scores use TransPositionalCategoricalScore where 50 = position average
 * Physical Score and GPR use global score (no positional version)
 * @param player - Player data fragment
 * @param isGoalkeeper - Whether to extract GK-specific metrics
 * @returns Object with positional scores for each metric, null for missing data
 */
export const extractPlayerPositionalScores = (
  player?: PlayerDetailPlayerMetaFragment | null,
  isGoalkeeper = false
): Record<string, number | null> => {
  if (!player?.seasonCategoricalScores?.length || !player?.bioData) {
    return isGoalkeeper ? GK_DEFAULT_SCORES : DEFAULT_SCORES;
  }

  // Get the most recent season's positional scores
  const latestSeason = player.seasonCategoricalScores[0];
  const bioData = player.bioData;
  const customScores = extractCustomMetricScores(
    bioData.customStats as BackendCustomStats | null | undefined
  );

  return withGlobalScores(
    buildCategoricalScores(
      definitionsForPosition(isGoalkeeper),
      latestSeason,
      "positional"
    ),
    safeRound(bioData.physicalScore),
    safeRound(bioData.gpr),
    customScores
  );
};

/**
 * Extract player scores for a specific season
 * Uses season-specific positional scores when a season key is provided,
 * falls back to global scores when no season is specified.
 * @param player - Player data fragment
 * @param seasonKey - Season key (e.g., "2024") for season-specific scores
 * @param isGoalkeeper - Whether to extract GK-specific metrics
 * @returns Object with scores for each metric for the selected season, null for missing data
 */
export const extractPlayerScoresForSeason = (
  player?: PlayerDetailPlayerMetaFragment | null,
  seasonKey?: string | null,
  isGoalkeeper = false
): Record<string, number | null> => {
  // No season = use global scores
  if (!seasonKey || !player?.bioData) {
    return extractPlayerScores(player, isGoalkeeper);
  }

  // Find the matching season
  const seasonData = player.seasonCategoricalScores?.find(
    s => String(s.ggUnifiedSeasonKey) === seasonKey
  );

  if (!seasonData) {
    // Fallback to global scores if season not found
    return extractPlayerScores(player, isGoalkeeper);
  }

  const bioData = player.bioData;
  const customScores = extractCustomMetricScores(
    bioData.customStats as BackendCustomStats | null | undefined
  );

  // Use season-specific positional scores
  return withGlobalScores(
    buildCategoricalScores(
      definitionsForPosition(isGoalkeeper),
      seasonData,
      "positional"
    ),
    safeRound(bioData.physicalScore),
    safeRound(bioData.gpr),
    customScores
  );
};

/**
 * Update selected metrics with current player scores
 * @param selectedMetrics - Currently selected metrics by position
 * @param playerScores - Current player's scores by metric label
 * @returns Selected metrics with updated values
 */
export const updateMetricsWithScores = (
  selectedMetrics: SelectedMetrics,
  playerScores: Record<string, number | null>
): SelectedMetrics =>
  Object.keys(selectedMetrics).reduce(
    (acc, key) => {
      const position = Number(key);
      const metric = selectedMetrics[position];
      if (!metric) return acc;
      return {
        ...acc,
        [position]: {
          label: metric.label,
          value: playerScores[metric.label] ?? null,
        },
      };
    },
    {} as Record<number, MetricOption>
  );

/**
 * Generate player data for scatter plot with current player
 * @param currentPlayerName - Name of the current player
 * @param xMetric - X-axis metric label
 * @param yMetric - Y-axis metric label
 * @param playerScores - Current player's scores
 * @param comparisonPlayers - Array of real comparison players with scores
 * @param comparisonPlayer - Optional single comparison player data for orange dot
 * @returns Array of player data points including current player and comparison players
 */
export const generateScatterPlotPlayers = (
  currentPlayerName: string,
  xMetric: string,
  yMetric: string,
  playerScores: Record<string, number | null>,
  comparisonPlayers: readonly {
    readonly id: string;
    readonly name: string;
    readonly scores: Record<string, number | null>;
  }[] = [],
  comparisonPlayer?: ComparisonPlayerData | null
): readonly PlayerDataPoint[] => {
  const otherPlayers: readonly PlayerDataPoint[] = comparisonPlayers.map(
    player => ({
      id: player.id,
      name: player.name,
      x: player.scores[xMetric] ?? null,
      y: player.scores[yMetric] ?? null,
      isCurrentPlayer: false,
      isComparisonPlayer: false,
    })
  );

  const currentPlayer: PlayerDataPoint = {
    id: "current-player",
    name: currentPlayerName,
    x: playerScores[xMetric] ?? null,
    y: playerScores[yMetric] ?? null,
    isCurrentPlayer: true,
    isComparisonPlayer: false,
  };

  const basePlayers: readonly PlayerDataPoint[] = [
    ...otherPlayers,
    currentPlayer,
  ];

  // Add comparison player if provided (immutably)
  const comparisonPoint: PlayerDataPoint | null = comparisonPlayer
    ? {
        id: comparisonPlayer.id,
        name: comparisonPlayer.name,
        x: comparisonPlayer.scores[xMetric] ?? null,
        y: comparisonPlayer.scores[yMetric] ?? null,
        isCurrentPlayer: false,
        isComparisonPlayer: true,
      }
    : null;

  return comparisonPoint ? [...basePlayers, comparisonPoint] : basePlayers;
};

/**
 * Radar data point type for chart visualization
 */
export interface RadarDataPointInput {
  readonly x: number;
  readonly y: number | null;
  readonly label: string;
}

/**
 * Build radar data points from selected metrics
 * @param selectedMetrics - Selected metrics with values by position
 * @param valueGetter - Function to get the value for each metric
 * @returns Array of radar data points with null values collapsed to 0 (chart center)
 */
export /**
 *
 */
const buildRadarData = (
  selectedMetrics: SelectedMetrics,
  valueGetter: (metric: MetricOption) => number | null
): readonly (RadarDataPointInput & { readonly y: number })[] => {
  const metrics = Object.keys(selectedMetrics)
    .map(Number)
    .sort((a, b) => a - b)
    .map(position => selectedMetrics[position])
    .filter((metric): metric is MetricOption => metric !== undefined);

  // Web grid is always hexagonal (6 axes). When fewer metrics exist (e.g. 5
  // for GK), pad with a zero-value point so the polygon maps correctly onto
  // the 6-axis grid instead of producing a 5-point shape on a 6-spoke layout.
  const AXIS_COUNT = 6;

  // Victory goes counter-clockwise, selectors go clockwise
  // Use reversed formula to mirror the direction while keeping TOP/BOTTOM aligned
  // Offset of 3 aligns: index 0 → x=4 (TOP), index 3 → x=1 (BOTTOM)
  const offset = 3;
  const dataPoints = metrics.map((metric, index) => ({
    x: ((AXIS_COUNT + offset - index) % AXIS_COUNT) + 1,
    y: valueGetter(metric) ?? 0,
    label: metric.label,
  }));

  // Fill remaining axes with zero-value points so Victory renders all 6 positions
  const usedXValues = new Set(dataPoints.map(d => d.x));
  const padding = Array.from({ length: AXIS_COUNT })
    .map((_, i) => i + 1)
    .filter(x => !usedXValues.has(x))
    .map(x => ({ x, y: 0, label: "" }));

  return [...dataPoints, ...padding];
};

/**
 * Decide what the Distribution card says about its comparison dots (SE-8283).
 *
 * The rows draw the player dot from data already on the page and the grey
 * comparison dots from a separate, heavier request. Without a caption, a row
 * waiting on that request, a row whose request failed, and a row whose group
 * simply has no scores all look the same: the player dot alone.
 * @param sourceStatus - Status of the comparison query feeding the dots
 * @param metrics - The distribution rows being drawn
 * @returns Which caption, if any, the card shows
 */
export const resolveDistributionComparisonStatus = (
  sourceStatus: ComparisonDataStatus,
  metrics: readonly { readonly distributionData: readonly BeeSwarmInput[] }[]
): DistributionComparisonStatus => {
  if (sourceStatus === "idle") return "hidden";
  if (sourceStatus === "loading") return "loading";
  if (sourceStatus === "error") return "error";
  // Only comparison entries carry a playerId; the player own dot does not.
  const hasComparisonDots = metrics.some(metric =>
    metric.distributionData.some(point => point.playerId != null)
  );
  return hasComparisonDots ? "hidden" : "empty";
};

/**
 * Build metrics for distribution panel
 * @param selectedMetrics - Selected metrics with values by position
 * @param comparisonPlayers - Array of comparison players with scores for distribution
 * @returns Array of metrics with distribution data including player info
 */
export const buildDistributionMetrics = (
  selectedMetrics: SelectedMetrics,
  comparisonPlayers: readonly DistributionPlayer[] = []
): readonly {
  label: string;
  value: number | null;
  distributionData: readonly BeeSwarmInput[];
}[] =>
  Object.keys(selectedMetrics)
    .map(Number)
    .sort((a, b) => a - b)
    .map(position => selectedMetrics[position])
    .filter((metric): metric is MetricOption => metric !== undefined)
    .map(metric => ({
      label: metric.label,
      value: metric.value,
      distributionData: getDistributionData(
        metric.label,
        metric.value,
        comparisonPlayers
      ),
    }));
